import React, { useState, useEffect } from 'react';
import { array, bool, func } from 'prop-types';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import classnames from 'classnames';
import DragAndDropTable from 'ps-components/DragAndDropTable/DragAndDropTable';
import FacetPanelLoading from 'ps-components/FacetPanel/FacetPanelLoading/FacetPanelLoading';
import ReactTooltip from 'react-tooltip';

import Placeholder from '../Placeholder/Placeholder';

import './ElementList.scss';
import { getElementShortName } from '../../../lib/formatters';
import { get } from 'lodash';

ElementList.propTypes = {
  disableGroupInput: func,
  includeAggregates: bool,
  isGroupingEnabled: bool,
  isReadOnly: bool,
  removeElement: func,
  selectedElements: array,
  setUnsavedEdit: func,
  treeLoaded: bool,
  treeLoading: bool,
  updateSelectedElements: func,
  showAggregates: bool,
  toggleSelectedAggType: func,
  useOverrideLabelFormatter: bool,
  hideRequiredFields: bool,
  hideAggs: bool
};

export default function ElementList(props) {
  const EMPLOYEE_TABLE_ID = 3;
  const AVG_AGGREGATE = {
    name: 'Avg',
    value: 'avg'
  };

  const ALL_OTHER_AGGREGATES = [
    {
      name: 'Median',
      value: 'median'
    },
    {
      name: 'Sum',
      value: 'sum'
    },
    {
      name: 'Min',
      value: 'min'
    },
    {
      name: 'Max',
      value: 'max'
    },
    {
      name: 'Count',
      value: 'count'
    },
    {
      name: 'Unique Count',
      value: 'count_distinct'
    }
  ];

  let AGGREGATE_TYPES =  props.hideAggs ? [AVG_AGGREGATE] : [AVG_AGGREGATE, ...ALL_OTHER_AGGREGATES];
  const DEFAULT_COLUMNS = [
    {
      Header: 'Column Heading',
      accessor: 'custom_name'
    },
    {
      Header: 'Data Element',
      accessor: 'text'
    }
  ];

  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  useEffect(() => {
    if (props.isGroupingEnabled) setColumns([...columns, { Header: 'Group', accessor: 'custom_group' }]);
  }, []);

  function getRowId(row) {
    return `${row.field_code}-${row.field_pk}-${row.aggregate_code}`;
  }

  // Note: elements with the same pk and different aggs will be updated together
  // they must have the same override and group (db tables not build for different labels)
  function changeOverrideLabel(element, value, defaultValue) {
    const newElements = [...props.selectedElements];

    newElements.forEach(e => {
      if (e.field_pk === element.field_pk) {
        e.override_label = value || defaultValue;
        props.setUnsavedEdit(true);
      }
    });

    props.updateSelectedElements(newElements);
  }

  function changeCustomGroup(element, value) {
    const newElements = [...props.selectedElements];

    newElements.forEach(e => {
      if (e.field_pk === element.field_pk) {
        if (!e.metadata) e.metadata = {};
        e.metadata.customGrouping = value;
        props.setUnsavedEdit(true);
      }
    });

    props.updateSelectedElements(newElements);
  }

  function getSubRow(row) {
    const element = row.original;

    if (
      !element?.aggregate_code ||
      (!element?.nonaggregate_data_sources?.includes(EMPLOYEE_TABLE_ID) && !element?.agg_codes)
    ) {
      return null;
    }

    const checkboxes = AGGREGATE_TYPES.map(agg => {
      const checked = element.agg_codes?.includes(agg.value);
      return (
        <div className="aggregate-type__checkbox-group" key={`agg-checkbox-group-${element.field_pk}-${agg.value}`}>
          <CheckboxInput
            className="aggregate-type__checkbox"
            checked={checked}
            key={`agg-checkbox-${element.field_pk}-${agg.value}`}
            onClick={() => {
              // At least one agg type must always be selected when removing
              if ((checked && element.agg_codes.length >= 2) || !checked) {
                props.toggleSelectedAggType(element, agg.value, !checked);
              }
            }}
          />
          <div className="aggregate-type__checkbox-label">{agg.name}</div>
        </div>
      );
    });

    return (
      <div className="aggregate-type__section" colSpan={3}>
        <div className="aggregate-type__title">Formula type:</div>
        {checkboxes}
      </div>
    );
  }

  // sometimes (for job search and slotted job search) fields will appear multiple times
  // but with different aggregate types; we only want to show those fields once, but
  // with appropriate aggregate checkboxes ticked
  function getElementsToDisplay(elements) {
    const newElements = [];

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      const alreadyExistsIndex = newElements.findIndex(ne => ne.field_pk === element.field_pk);

      if (!element?.aggregate_code || !element?.nonaggregate_data_sources?.includes(EMPLOYEE_TABLE_ID)) {
        newElements.push(element);
      } else {
        if (alreadyExistsIndex > -1) {
          newElements[alreadyExistsIndex].agg_codes.push(element.aggregate_code);
        } else {
          element.agg_codes = [element.aggregate_code];
          newElements.push(element);
        }
      }
    }

    return [...newElements];
  }

  function getDataRow(row) {
    const element = row.original;
    let elementNameToDisplay = element?.text;
    let defaultValue = element?.text;
    const keyName = `element-list-${getRowId(element)}`;

    if (
      element?.default_title &&
      !element?.default_title?.includes('${') &&
      !element?.default_title?.includes('User Defined Field')
    ) {
      elementNameToDisplay = element?.default_title;
      defaultValue = element?.default_title;
    }
    let columnHeadingValue = element.override_label || element.text;
    
    if(props.useOverrideLabelFormatter) {
      columnHeadingValue = getElementShortName(element.default_title);
      if (columnHeadingValue === element.default_title)  columnHeadingValue = getElementShortName(element.text);
    }

    return (
      <>
        <td>
          {props.useOverrideLabelFormatter ? (
            <div className="element-list__column-heading-text">
              <div className="data-element__text">{columnHeadingValue}</div>
            </div>
          ) : (
            <input
              type="text"
              className="element-list__column-heading"
              value={columnHeadingValue}
              onChange={e => changeOverrideLabel(element, e.target.value, defaultValue)}
              disabled={props.isReadOnly}
            />
          )}
        </td>
        <td>
          <div className="element-list__data-element">
            <div className="data-element__text">{elementNameToDisplay}</div>
            {element.qtip && (
              <div>
                <span className="icon-reverse icon-circle data-element__formula-icon">
                  <i
                    className="icon-info"
                    data-row-key={`tooltip-${keyName}`}
                    key={`tooltip-${keyName}`}
                    data-tip={element.qtip}
                    data-html={true}
                  />
                </span>
                <ReactTooltip data-row-key={`tooltip-${keyName}`} key={`tooltip-${keyName}`} place="bottom" />
              </div>
            )}
          </div>
        </td>
        <td>
          {!element.mandatory_field && (
            <input
              type="text"
              className={classnames('element-list__group', { hidden: !props.isGroupingEnabled })}
              value={element.metadata?.customGrouping || undefined}
              onChange={e => changeCustomGroup(element, e.target.value)}
              disabled={props.isReadOnly || (props.disableGroupInput && props.disableGroupInput(element))}
            />
          )}

          <div className="element-list__delete">
            {!element.mandatory_field && !props.isReadOnly && (
              <i className="icon-trash" onClick={() => props.removeElement(element)} />
            )}
          </div>
        </td>
      </>
    );
  }

  function updateData(data) {
    props.updateSelectedElements(data);
  }

  function getEmptyPlaceholder() {
    if (props.treeLoading || !props.treeLoaded) {
      return (
        <FacetPanelLoading
          className="element-list__loading"
          numberOfFacets={1}
          numberOfSubItems={7}
          includeHeader={false}
        />
      );
    }

    return (
      <Placeholder
        containerClassName="element-list__empty-placeholder"
        primaryText="No Elements Selected"
        secondaryText="Please select elements from the list to the left"
      />
    );
  }

  function showPlaceholder() {
    if (props.treeLoading || props.selectedElements.length === 0) return true;
    if (props.hideRequiredFields && props.selectedElements.every(e => e.mandatory_field)) return true;
    return false;
  }

  return (
    <div className="configure-views__element-list">
      { showPlaceholder() ? (
        getEmptyPlaceholder()
      ) : (
        <>
          <div className="element-list__title">Selected Data Elements</div>

          <DragAndDropTable
            columns={columns}
            data={getElementsToDisplay(props.selectedElements)}
            getRowId={getRowId}
            getDataRow={getDataRow}
            getSubRow={props.showAggregates ? getSubRow : null}
            updateData={updateData}
            showMove={!props.isReadOnly}
            hideRequiredFields={props.hideRequiredFields}
          />
        </>
      )}
    </div>
  );
}
